/**
 * The deformable background lattice.
 *
 * This is the effect the whole look hangs on. A static grid reads as wallpaper;
 * a grid that recoils from every explosion reads as a physical surface the
 * fight is happening *on*. Three details make it work, and all three are here:
 *
 *  1. **Waves travel.** Nodes are coupled to their neighbours, not just to
 *     their rest position, so an impulse propagates outward as a ring instead
 *     of denting one cell and stopping. Neighbour springs are stiffer than the
 *     anchor spring; that ratio is what sets how far a ripple carries.
 *  2. **Stress glows.** Line brightness comes from how far each edge is
 *     stretched from its rest length, not from a constant. Emissive output
 *     climbs past 1.0 exactly where the lattice is being torn, so the bloom
 *     pass paints the deformation instead of the geometry.
 *  3. **Impulses have a shape.** A 1/r² kick explodes at the centre and looks
 *     like a spike. This uses a smoothstep with a soft core, so a shockwave
 *     leaves a ring, which is what a shockwave actually is.
 *
 * Integration is semi-implicit Euler, substepped to whatever the configured
 * stiffness needs. Everything lives in typed arrays; `step` and `sync` do not
 * allocate.
 */

import * as THREE from 'three';
import { GRID, SIM, VIEW } from '@/core/config';
import { perf } from '@/core/perf';
import { region } from '@/core/region';
import { PHOSPHOR, ROLE } from '@/core/palette';
import type { RenderSystem, System, World } from '@/core/types';

/** Concurrent sustained shockwaves (black holes, boss auras). Impulses are free. */
const MAX_WAVES = 24;

/**
 * Fraction of the radius over which the impulse ramps up from the epicentre.
 *
 * Without it the direction vector is singular at the centre and one unlucky
 * node gets flung. Kept wide on purpose: a narrow core tears the four edges
 * spanning the epicentre apart while everything else stays put, and a 16px
 * lattice renders that as a hard white square. A wide core moves the whole
 * middle coherently and pushes the tear out to a radius with enough nodes on
 * it to read as a ring.
 */
const IMPULSE_CORE = 0.36;

/** Largest `dt * omega` we trust for semi-implicit Euler. Substeps follow from it. */
const STABILITY_LIMIT = 0.25;

/** Diagonal spring stiffness relative to the structural springs. */
const SHEAR_SCALE = 0.5;

export interface WarpGridOptions {
  /** Node spacing in buffer pixels. Defaults to `GRID.spacingPx`. */
  spacing?: number;
  /**
   * Fixed lattice extent, in buffer pixels, anchored at the band's origin.
   *
   * Omit both and the lattice covers the *rendered region* and follows it across
   * a resize, which is what the game wants: the region is the whole of what the
   * camera shows, and a lattice that stopped at the 640x360 band would leave the
   * extra glass on a tall window black. Pass them to pin the sheet to a fixed
   * size — a test that wants a lattice it can count the nodes of.
   */
  width?: number;
  height?: number;
}

export class WarpGrid implements System, RenderSystem {
  readonly name = 'warpGrid';

  /**
   * Add this to the scene. Draws as one indexed `Mesh` call.
   *
   * Every edge is a quad, not a GL line. A 1px `LineSegments` in a 640x360
   * buffer rasterises a deformed near-horizontal edge as a staircase — the
   * line holds a row, jumps, and the diamond-exit rule bridges the jump with a
   * short vertical stub. That artefact survived shrinking `maxDisplacementPx`
   * from 22 to 9 because displacement was never its cause; it is the line
   * rasteriser, and the only way out is to stop using it. Quads also let a
   * stressed edge get physically wider, which a GL line cannot do portably.
   */
  readonly object: THREE.Mesh;

  // ── lattice topology ──
  //
  // Not `readonly`: the sheet is rebuilt whenever the rendered region changes
  // shape, which is a window resize and nothing else. Every array below is sized
  // from the extent, so there is no way to grow the sheet in place — and no
  // reason to want one, since the alternative is allocating for the largest
  // window the player might ever open and solving springs nobody can see.
  private nx = 0;
  private ny = 0;
  private sx = 0;
  private sy = 0;
  private nodeCount = 0;
  /** Every spring the solver sees, structural and shear. */
  private edgeCount = 0;
  /** Leading slice of the edge arrays that gets drawn. Shear springs do not. */
  private renderEdges = 0;
  /** World position of the lattice's top-left node. Zero for the bare band. */
  private originX = 0;
  private originY = 0;

  // ── node state (SoA) ──
  private posX!: Float32Array;
  private posY!: Float32Array;
  private prevX!: Float32Array;
  private prevY!: Float32Array;
  private velX!: Float32Array;
  private velY!: Float32Array;
  private restX!: Float32Array;
  private restY!: Float32Array;
  private accX!: Float32Array;
  private accY!: Float32Array;
  private pinned!: Uint8Array;

  // ── edges ──
  private edgeA!: Uint32Array;
  private edgeB!: Uint32Array;
  private edgeRest!: Float32Array;
  /** Per-edge stiffness multiplier. Shear springs sit below the structural ones. */
  private edgeK!: Float32Array;

  // ── sustained shockwaves ──
  private readonly waveX = new Float32Array(MAX_WAVES);
  private readonly waveY = new Float32Array(MAX_WAVES);
  private readonly waveS = new Float32Array(MAX_WAVES);
  private readonly waveR = new Float32Array(MAX_WAVES);
  private readonly waveT = new Float32Array(MAX_WAVES);
  private waveCount = 0;

  // ── GPU buffers ──
  private vertexPos!: Float32Array;
  private vertexStress!: Float32Array;
  /** Per-vertex `(signed distance across the edge, half-width)`, both in px. */
  private vertexLine!: Float32Array;
  private posAttr!: THREE.BufferAttribute;
  private stressAttr!: THREE.BufferAttribute;
  private lineAttr!: THREE.BufferAttribute;
  private geometry!: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;

  /** Interpolated node positions, rebuilt each `sync`. */
  private drawX!: Float32Array;
  private drawY!: Float32Array;

  private readonly substeps: number;
  private shockCursor = 0;

  /**
   * Which render edges are drawn, under the governor's line decimation.
   *
   * Spacing cannot change at runtime — every typed array in this file is sized
   * from it — so the coarse lattice is made by dropping alternate rows and
   * columns of lines instead. The solver keeps running at 16px, which is what
   * keeps a ripple travelling the way it did before the rung was pulled; only
   * the drawn line count halves, and with it the fill this pass costs.
   *
   * The first and last row and column are always kept: the border is welded to
   * the bezel in every reference frame and a missing edge line reads as a bug.
   */
  private edgeVisible!: Uint8Array;
  private stride = 1;
  private perfGeneration = perf.generation;
  /** Last region generation the sheet was built against. */
  private regionGeneration = -1;
  /** Extent override from the options, or null to follow the region. */
  private readonly fixedExtent: { width: number; height: number } | null;
  private readonly spacing: number;

  // ── live tuning knobs ──
  /**
   * Neighbour spring constant. Above the anchor constant, ripples travel; below
   * it, they die where they start.
   *
   * This ran at 1.6x, i.e. 41.6 against an anchor of 26, and with eight springs
   * on every interior node the sheet was effectively rigid: an impulse of 260
   * translated the neighbourhood instead of deforming it, and `shockwave.png`
   * came back flat. Below the anchor constant the lattice can actually take a
   * dent, which is the whole point of it being a lattice.
   */
  linkStiffness = GRID.stiffness * 0.55;
  /** Anchor spring pulling a node home. */
  anchorStiffness = GRID.stiffness;
  /** Velocity damping, per second. */
  damping = GRID.damping;
  /** Edge stretch fraction that counts as fully stressed. */
  stretchGain = GRID.stretchGain;
  baseIntensity = GRID.baseIntensity;
  /**
   * Saturation term on the stretch response. The curve is
   * `s = stretchGain * strain / (1 + stretchKnee * strain)`, so `stretchGain`
   * is the slope at zero strain and `stretchGain / stretchKnee` is the ceiling
   * it approaches. 0 gives the raw linear deviation.
   *
   * Paired with `GRID.stretchGain` 26 this asymptotes at 5.2, so the broad,
   * gently strained field lights while an edge spanning an epicentre still has
   * somewhere left to go and saturates to white.
   */
  stretchKnee = 5.0;
  /** Strain floor below which an edge counts as at rest. See `GRID.stretchFoot`. */
  stretchFoot = GRID.stretchFoot;
  /**
   * Half-width of a resting edge quad, in buffer pixels. Tuned so the coverage
   * integral across a resting edge comes to ~0.9px — just under the 1px the GL
   * line rasteriser used to lay down, which keeps the empty frame's black floor
   * where round three left it while the edge itself is now antialiased.
   */
  lineHalfWidth = 0.65;
  /**
   * Extra half-width per unit of stress. A torn edge in
   * gwre2_grid-shockwave-fullfield is visibly fatter than a resting one, and
   * width is most of why its lattice survives a blur at p99 0.93 where a 1px
   * line cannot.
   */
  stressWidth = 0.30;

  constructor(opts: WarpGridOptions = {}) {
    this.spacing = Math.max(4, opts.spacing ?? GRID.spacingPx);
    this.fixedExtent = opts.width !== undefined || opts.height !== undefined
      ? {
        width: opts.width ?? VIEW.width,
        height: opts.height ?? VIEW.height - VIEW.hudRows * VIEW.tile,
      }
      : null;

    const base = PHOSPHOR[ROLE.grid];
    const hot = PHOSPHOR[ROLE.gridStressed];
    const white = PHOSPHOR.white;
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uBase: { value: new THREE.Vector3(base[0], base[1], base[2]) },
        uHot: { value: new THREE.Vector3(hot[0], hot[1], hot[2]) },
        uWhite: { value: new THREE.Vector3(white[0], white[1], white[2]) },
        uBaseIntensity: { value: this.baseIntensity },
      },
      vertexShader: GRID_VERT,
      fragmentShader: GRID_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
      blendEquation: THREE.AddEquation,
      // The playfield camera flips Y, which inverts the quad winding. The
      // lattice refuses to care which way round it is.
      side: THREE.DoubleSide,
    });

    // The mesh outlives every rebuild: `main.ts` adds it to the scene once and
    // a resize must not require it to be re-added. `build` swaps the geometry
    // underneath it.
    this.object = new THREE.Mesh(new THREE.BufferGeometry(), this.material);
    this.object.frustumCulled = false;
    this.object.matrixAutoUpdate = false;
    // Behind sprites, particles and trails. The lattice is the floor.
    this.object.renderOrder = -100;
    this.object.name = 'warpGrid';

    // Worst-case eigenvalue for a node with four links, then the substep count
    // that keeps dt*omega under the explicit-integrator limit. Retuning the
    // config cannot make this blow up; it only costs another substep. Extent
    // does not enter it, so it is settled once rather than on every rebuild.
    const kMax = this.anchorStiffness + 8 * this.linkStiffness * (1 + SHEAR_SCALE);
    const omega = Math.sqrt(kMax / Math.max(0.01, GRID.mass));
    this.substeps = Math.min(8, Math.max(1, Math.ceil((SIM.dt * omega) / STABILITY_LIMIT)));

    this.build();
  }

  /**
   * Lays the sheet out over the current extent, from nothing.
   *
   * Called once from the constructor and again whenever the rendered region
   * changes shape. Everything the solver touches is sized from the extent, so
   * this reallocates rather than resizes — and the sheet resets to rest while it
   * is at it, which is the honest thing to do: a ripple mid-flight across a
   * lattice that just changed its node count has nowhere to be.
   */
  private build(): void {
    const spacing = this.spacing;
    const extent = this.fixedExtent;
    const view = region.current;
    const w = extent?.width ?? view.width;
    const h = extent?.height ?? view.height;
    this.originX = extent === null ? view.x0 : 0;
    this.originY = extent === null ? view.y0 : 0;
    this.regionGeneration = region.generation;

    // Cell counts are rounded so the border lands exactly on the playfield edge.
    // 360 / 16 is not an integer; a 15.65px row beats a ragged last row.
    const cellsX = Math.max(1, Math.round(w / spacing));
    const cellsY = Math.max(1, Math.round(h / spacing));
    this.nx = cellsX + 1;
    this.ny = cellsY + 1;
    this.sx = w / cellsX;
    this.sy = h / cellsY;

    const n = this.nx * this.ny;
    this.nodeCount = n;
    this.renderEdges = this.ny * (this.nx - 1) + (this.ny - 1) * this.nx;
    // Shear springs run both diagonals of every cell. Without them the lattice
    // only resists stretching along its own axes, so an explosion propagates as
    // a plus sign instead of a ring — the single most obvious tell that a grid
    // is a spring toy rather than a surface.
    this.edgeCount = this.renderEdges + 2 * (this.nx - 1) * (this.ny - 1);

    this.posX = new Float32Array(n);
    this.posY = new Float32Array(n);
    this.prevX = new Float32Array(n);
    this.prevY = new Float32Array(n);
    this.velX = new Float32Array(n);
    this.velY = new Float32Array(n);
    this.restX = new Float32Array(n);
    this.restY = new Float32Array(n);
    this.accX = new Float32Array(n);
    this.accY = new Float32Array(n);
    this.pinned = new Uint8Array(n);
    this.drawX = new Float32Array(n);
    this.drawY = new Float32Array(n);

    for (let r = 0; r < this.ny; r++) {
      for (let c = 0; c < this.nx; c++) {
        const i = r * this.nx + c;
        const x = this.originX + c * this.sx;
        const y = this.originY + r * this.sy;
        this.restX[i] = x;
        this.restY[i] = y;
        this.posX[i] = x;
        this.posY[i] = y;
        this.prevX[i] = x;
        this.prevY[i] = y;
        this.drawX[i] = x;
        this.drawY[i] = y;
        // Border nodes are pinned: the lattice must stay welded to the bezel.
        if (r === 0 || c === 0 || r === this.ny - 1 || c === this.nx - 1) {
          this.pinned[i] = 1;
        }
      }
    }

    this.edgeA = new Uint32Array(this.edgeCount);
    this.edgeB = new Uint32Array(this.edgeCount);
    this.edgeRest = new Float32Array(this.edgeCount);
    this.edgeK = new Float32Array(this.edgeCount);
    let e = 0;
    for (let r = 0; r < this.ny; r++) {
      for (let c = 0; c < this.nx - 1; c++) {
        this.edgeA[e] = r * this.nx + c;
        this.edgeB[e] = r * this.nx + c + 1;
        this.edgeRest[e] = this.sx;
        this.edgeK[e] = 1;
        e++;
      }
    }
    for (let r = 0; r < this.ny - 1; r++) {
      for (let c = 0; c < this.nx; c++) {
        this.edgeA[e] = r * this.nx + c;
        this.edgeB[e] = (r + 1) * this.nx + c;
        this.edgeRest[e] = this.sy;
        this.edgeK[e] = 1;
        e++;
      }
    }
    const diag = Math.sqrt(this.sx * this.sx + this.sy * this.sy);
    for (let r = 0; r < this.ny - 1; r++) {
      for (let c = 0; c < this.nx - 1; c++) {
        const tl = r * this.nx + c;
        this.edgeA[e] = tl;
        this.edgeB[e] = tl + this.nx + 1;
        this.edgeRest[e] = diag;
        this.edgeK[e] = SHEAR_SCALE;
        e++;
        this.edgeA[e] = tl + 1;
        this.edgeB[e] = tl + this.nx;
        this.edgeRest[e] = diag;
        this.edgeK[e] = SHEAR_SCALE;
        e++;
      }
    }

    this.edgeVisible = new Uint8Array(this.renderEdges).fill(1);
    this.vertexPos = new Float32Array(this.renderEdges * 4 * 3);
    this.vertexStress = new Float32Array(this.renderEdges * 4);
    this.vertexLine = new Float32Array(this.renderEdges * 4 * 2);
    this.posAttr = new THREE.BufferAttribute(this.vertexPos, 3);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.stressAttr = new THREE.BufferAttribute(this.vertexStress, 1);
    this.stressAttr.setUsage(THREE.DynamicDrawUsage);
    this.lineAttr = new THREE.BufferAttribute(this.vertexLine, 2);
    this.lineAttr.setUsage(THREE.DynamicDrawUsage);

    // Winding is fixed for the life of the mesh; only the four corners move.
    const index = new Uint32Array(this.renderEdges * 6);
    for (let e = 0; e < this.renderEdges; e++) {
      const v = e * 4;
      const o = e * 6;
      index[o] = v;
      index[o + 1] = v + 1;
      index[o + 2] = v + 2;
      index[o + 3] = v + 2;
      index[o + 4] = v + 1;
      index[o + 5] = v + 3;
    }

    const previous = this.geometry as THREE.BufferGeometry | undefined;
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', this.posAttr);
    this.geometry.setAttribute('aStress', this.stressAttr);
    this.geometry.setAttribute('aLine', this.lineAttr);
    this.geometry.setIndex(new THREE.BufferAttribute(index, 1));
    // Positions are rewritten every frame; a stale bounding sphere would cull us.
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
    this.object.geometry = this.geometry;
    previous?.dispose();

    // A rebuild hands back a lattice that has never heard of the governor's
    // decimation, so whatever stride is being held has to be re-applied to the
    // fresh mask rather than skipped as already-current.
    const held = this.stride;
    this.stride = 1;
    this.setStride(held);

    this.writeVertices();
  }

  // ───────────────────────────────────────────────────────────────── forces ──

  /**
   * Radial impulse. Positive strength shoves outward, negative drags inward —
   * that is the black hole, and it is the same code path.
   *
   * @param strength peak velocity change in px/s at the ring crest
   * @param radius   falloff radius in buffer pixels
   */
  applyShockwave(x: number, y: number, strength: number, radius: number): void {
    if (radius <= 0 || strength === 0) return;
    const invMass = 1 / Math.max(0.01, GRID.mass);
    const r2 = radius * radius;
    const invR = 1 / radius;
    const invCore = 1 / IMPULSE_CORE;

    // Only touch nodes whose rest position could be inside the radius; the
    // displacement clamp bounds how far a node can have wandered from it.
    // Cell indices are relative to the sheet's own origin, which is not the
    // band's once the region extends past it.
    const pad = GRID.maxDisplacementPx;
    const lx = x - this.originX;
    const ly = y - this.originY;
    const c0 = Math.max(0, Math.floor((lx - radius - pad) / this.sx));
    const c1 = Math.min(this.nx - 1, Math.ceil((lx + radius + pad) / this.sx));
    const r0 = Math.max(0, Math.floor((ly - radius - pad) / this.sy));
    const r1 = Math.min(this.ny - 1, Math.ceil((ly + radius + pad) / this.sy));

    for (let r = r0; r <= r1; r++) {
      const row = r * this.nx;
      for (let c = c0; c <= c1; c++) {
        const i = row + c;
        if (this.pinned[i] !== 0) continue;
        const dx = this.posX[i] - x;
        const dy = this.posY[i] - y;
        const d2 = dx * dx + dy * dy;
        if (d2 > r2) continue;
        const d = Math.sqrt(d2);
        if (d < 1e-4) continue;
        const t = d * invR;
        // 1 at the centre, 0 at the rim, C1 at both ends.
        const falloff = 1 - t * t * (3 - 2 * t);
        // Soft core turns the spike into a ring.
        const core = t < IMPULSE_CORE ? t * invCore : 1;
        const kick = strength * falloff * core * invMass;
        const inv = 1 / d;
        this.velX[i] += dx * inv * kick;
        this.velY[i] += dy * inv * kick;
      }
    }
  }

  /**
   * Sustained pull or push. Used for black holes and boss auras: the force is
   * reapplied every step until `duration` runs out.
   */
  addSustained(x: number, y: number, strength: number, radius: number, duration: number): void {
    if (duration <= 0) {
      this.applyShockwave(x, y, strength, radius);
      return;
    }
    if (this.waveCount >= MAX_WAVES) return;
    const i = this.waveCount++;
    this.waveX[i] = x;
    this.waveY[i] = y;
    this.waveS[i] = strength;
    this.waveR[i] = radius;
    this.waveT[i] = duration;
  }

  /** Snaps every node home and drops every sustained wave. */
  reset(): void {
    this.posX.set(this.restX);
    this.posY.set(this.restY);
    this.prevX.set(this.restX);
    this.prevY.set(this.restY);
    this.velX.fill(0);
    this.velY.fill(0);
    this.waveCount = 0;
    this.shockCursor = 0;
    this.writeVertices();
  }

  // ─────────────────────────────────────────────────────────────────── step ──

  step(dt: number, world: World): void {
    this.ingest(world);
    this.prevX.set(this.posX);
    this.prevY.set(this.posY);

    // Sustained wells push once per sim step, scaled by dt so their strength
    // reads as a force rather than as an impulse.
    for (let i = 0; i < this.waveCount; i++) {
      this.applyShockwave(this.waveX[i], this.waveY[i], this.waveS[i] * dt, this.waveR[i]);
      this.waveT[i] -= dt;
      if (this.waveT[i] <= 0) {
        const last = --this.waveCount;
        this.waveX[i] = this.waveX[last];
        this.waveY[i] = this.waveY[last];
        this.waveS[i] = this.waveS[last];
        this.waveR[i] = this.waveR[last];
        this.waveT[i] = this.waveT[last];
        i--;
      }
    }

    const h = dt / this.substeps;
    for (let s = 0; s < this.substeps; s++) this.integrate(h);
  }

  /** Drains new shockwaves out of the world queue exactly once each. */
  private ingest(world: World): void {
    const q = world.fx.shockwaves;
    // `clearFx` empties the queue every frame, which is how the cursor resets.
    if (q.length < this.shockCursor) this.shockCursor = 0;
    for (let i = this.shockCursor; i < q.length; i++) {
      const s = q[i];
      if (s.duration > 0) this.addSustained(s.x, s.y, s.strength, s.radius, s.duration);
      else this.applyShockwave(s.x, s.y, s.strength, s.radius);
    }
    this.shockCursor = q.length;
  }

  private integrate(h: number): void {
    const { posX, posY, velX, velY, accX, accY, restX, restY, pinned } = this;
    const { edgeA, edgeB, edgeRest, edgeK } = this;
    const ks = this.linkStiffness;
    const ka = this.anchorStiffness;
    const kd = this.damping;
    const invMass = 1 / Math.max(0.01, GRID.mass);
    const maxD = GRID.maxDisplacementPx;
    const maxD2 = maxD * maxD;

    accX.fill(0);
    accY.fill(0);

    for (let e = 0, n = this.edgeCount; e < n; e++) {
      const a = edgeA[e];
      const b = edgeB[e];
      const dx = posX[b] - posX[a];
      const dy = posY[b] - posY[a];
      const len2 = dx * dx + dy * dy;
      if (len2 < 1e-10) continue;
      const len = Math.sqrt(len2);
      const f = (ks * edgeK[e] * (len - edgeRest[e])) / len;
      const fx = dx * f;
      const fy = dy * f;
      accX[a] += fx;
      accY[a] += fy;
      accX[b] -= fx;
      accY[b] -= fy;
    }

    for (let i = 0, n = this.nodeCount; i < n; i++) {
      if (pinned[i] !== 0) continue;
      const vx = velX[i];
      const vy = velY[i];
      const ax = (accX[i] + ka * (restX[i] - posX[i]) - kd * vx) * invMass;
      const ay = (accY[i] + ka * (restY[i] - posY[i]) - kd * vy) * invMass;

      // Semi-implicit: velocity first, then position from the new velocity.
      let nvx = vx + ax * h;
      let nvy = vy + ay * h;
      let nx = posX[i] + nvx * h;
      let ny = posY[i] + nvy * h;

      const dx = nx - restX[i];
      const dy = ny - restY[i];
      const d2 = dx * dx + dy * dy;
      if (d2 > maxD2) {
        // Park the node on the clamp boundary and kill the outward component of
        // its velocity, otherwise it grinds against the limit for a full second.
        const inv = maxD / Math.sqrt(d2);
        nx = restX[i] + dx * inv;
        ny = restY[i] + dy * inv;
        const ndx = dx * inv * (1 / maxD);
        const ndy = dy * inv * (1 / maxD);
        const outward = nvx * ndx + nvy * ndy;
        if (outward > 0) {
          nvx -= ndx * outward;
          nvy -= ndy * outward;
        }
      }

      velX[i] = nvx;
      velY[i] = nvy;
      posX[i] = nx;
      posY[i] = ny;
    }
  }

  // ─────────────────────────────────────────────────────────────────── sync ──

  sync(_world: World, alpha: number): void {
    // The window changed shape, so the sheet has a different amount of world to
    // cover. Checked here rather than pushed from `Renderer` for the same reason
    // the governor is: `core/*` is readable by everyone and a render system that
    // had to be wired up by `main.ts` is a render system that can be forgotten.
    if (this.fixedExtent === null && this.regionGeneration !== region.generation) {
      this.build();
    }
    if (this.perfGeneration !== perf.generation) {
      this.perfGeneration = perf.generation;
      this.setStride(perf.caps.gridStride);
    }
    const a = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
    const { posX, posY, prevX, prevY, drawX, drawY } = this;
    for (let i = 0, n = this.nodeCount; i < n; i++) {
      drawX[i] = prevX[i] + (posX[i] - prevX[i]) * a;
      drawY[i] = prevY[i] + (posY[i] - prevY[i]) * a;
    }
    this.writeVertices();
  }

  /**
   * Rebuilds the visibility mask and collapses whatever the new stride drops.
   *
   * Hidden quads keep their index block and their vertex slots — re-indexing
   * would mean rebuilding the index buffer — but their four corners sit on one
   * point, so they rasterise nothing. Zeroed once here rather than every frame.
   */
  private setStride(stride: number): void {
    const s = stride >= 2 ? Math.floor(stride) : 1;
    if (s === this.stride) return;
    this.stride = s;

    const horiz = this.ny * (this.nx - 1);
    for (let e = 0; e < this.renderEdges; e++) {
      let keep: boolean;
      if (e < horiz) {
        const r = Math.floor(e / (this.nx - 1));
        keep = s === 1 || r % s === 0 || r === this.ny - 1;
      } else {
        const c = (e - horiz) % this.nx;
        keep = s === 1 || c % s === 0 || c === this.nx - 1;
      }
      this.edgeVisible[e] = keep ? 1 : 0;
      if (keep) continue;
      const p = e * 12;
      for (let k = 0; k < 12; k++) this.vertexPos[p + k] = 0;
      const so = e * 4;
      for (let k = 0; k < 4; k++) this.vertexStress[so + k] = 0;
      const lo = e * 8;
      for (let k = 0; k < 8; k++) this.vertexLine[lo + k] = 0;
    }
  }

  /**
   * Rebuilds the line batch. Stress is the fractional deviation of each edge
   * from its rest length, written to both of its vertices — an edge that is
   * being pulled apart lights up along its whole length.
   */
  private writeVertices(): void {
    const { drawX, drawY, edgeA, edgeB, edgeRest, vertexPos, vertexStress, vertexLine } = this;
    const gain = this.stretchGain;
    const knee = this.stretchKnee;
    const foot = this.stretchFoot;
    const visible = this.edgeVisible;
    const decimated = this.stride > 1;
    for (let e = 0, n = this.renderEdges; e < n; e++) {
      if (decimated && visible[e] === 0) continue;
      const a = edgeA[e];
      const b = edgeB[e];
      const ax = drawX[a];
      const ay = drawY[a];
      const bx = drawX[b];
      const by = drawY[b];

      const dx = bx - ax;
      const dy = by - ay;
      const len = Math.sqrt(dx * dx + dy * dy);
      const rest = edgeRest[e];
      const strain = (len > rest ? len - rest : rest - len) / rest;
      // Saturating response. Compression counts the same as extension: a cell
      // squeezed by a passing front is under just as much stress as one being
      // pulled apart, and lighting only the leading half of a wave halves the
      // effect. An edge spanning an epicentre can strain 300%, so the knee is
      // what keeps that single edge from pinning to flat white and reading as a
      // painted line. Set `stretchKnee = 0` for the unshaped response.
      const shaped = knee > 0 ? strain / (1 + strain * knee) : strain;
      // Stress floor. `gain` is a slope at zero, so without this every edge in
      // the frame is paid something the instant the sheet moves, and the broad
      // low-strain field behind a blast lights as hard as the front. Gating on
      // strain rather than on the shaped value keeps the ceiling exactly where
      // it was: past the floor the smoothstep is 1 and this term disappears.
      const t = foot > 0 ? (strain < foot ? strain / foot : 1) : 1;
      let s = shaped * gain * t * t * (3 - 2 * t);
      // Headroom for the wave front. The shader blows out to white from 1.8,
      // and at a ceiling of 3 it only ever got 36% of the way there, so the
      // most violently torn edge in the frame rendered the same cyan as the
      // gently strained field two cells behind it.
      if (s > 5) s = 5;

      // Quad corners. The perpendicular is degenerate for a collapsed edge, so
      // fall back to +y rather than emitting a NaN into the vertex buffer.
      const inv = len > 1e-5 ? 1 / len : 0;
      const hw = this.lineHalfWidth + this.stressWidth * (s > 3 ? 3 : s);
      const nx = -dy * inv * hw;
      const ny = dx * inv * hw;

      const p = e * 12;
      vertexPos[p] = ax + nx;
      vertexPos[p + 1] = ay + ny;
      vertexPos[p + 2] = 0;
      vertexPos[p + 3] = ax - nx;
      vertexPos[p + 4] = ay - ny;
      vertexPos[p + 5] = 0;
      vertexPos[p + 6] = bx + nx;
      vertexPos[p + 7] = by + ny;
      vertexPos[p + 8] = 0;
      vertexPos[p + 9] = bx - nx;
      vertexPos[p + 10] = by - ny;
      vertexPos[p + 11] = 0;

      const so = e * 4;
      vertexStress[so] = s;
      vertexStress[so + 1] = s;
      vertexStress[so + 2] = s;
      vertexStress[so + 3] = s;

      const lo = e * 8;
      vertexLine[lo] = hw;
      vertexLine[lo + 1] = hw;
      vertexLine[lo + 2] = -hw;
      vertexLine[lo + 3] = hw;
      vertexLine[lo + 4] = hw;
      vertexLine[lo + 5] = hw;
      vertexLine[lo + 6] = -hw;
      vertexLine[lo + 7] = hw;
    }
    this.posAttr.needsUpdate = true;
    this.stressAttr.needsUpdate = true;
    this.lineAttr.needsUpdate = true;
    this.material.uniforms.uBaseIntensity.value = this.baseIntensity;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }

  /** Node counts, for the debug overlay. */
  get stats(): { nodes: number; springs: number; lines: number; substeps: number } {
    return { nodes: this.nodeCount, springs: this.edgeCount, lines: this.renderEdges, substeps: this.substeps };
  }
}

const GRID_VERT = /* glsl */ `
attribute float aStress;
attribute vec2 aLine;
varying float vStress;
varying vec2 vLine;

void main() {
  vStress = aStress;
  vLine = aLine;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const GRID_FRAG = /* glsl */ `
precision highp float;

uniform vec3 uBase;
uniform vec3 uHot;
uniform vec3 uWhite;
uniform float uBaseIntensity;

varying float vStress;
varying vec2 vLine;

void main() {
  float s = vStress;

  // Analytic coverage across the edge. vLine.x is the signed distance from
  // the centreline in buffer pixels, vLine.y the quad's half-width. A solid
  // core out to 0.25px with a soft shoulder to the rim is what an antialiased
  // 1px line looks like, and it is why a deformed near-horizontal edge no
  // longer staircases: coverage varies continuously instead of a pixel being
  // in or out.
  float hw = max(vLine.y, 0.35);
  float cover = 1.0 - smoothstep(0.25, hw, abs(vLine.x));
  if (cover <= 0.002) discard;

  // Hue walks up the palette with stress, then blows out to white at the top.
  // "Hot" used to mean "white" from s = 1.0, which is the same mistake the
  // sprite shader made: a stressed cell should go *more saturated*, not
  // paler. Pushed out to 1.8 and softened so a torn cell reads as bright cyan
  // along its length with a white core only where it is genuinely being ripped.
  vec3 c = mix(uBase, uHot, clamp(s, 0.0, 1.0));
  c = mix(c, uWhite, clamp((s - 1.5) * 0.42, 0.0, 1.0));

  // Emissive output. The lattice is a light source, not graph paper: at rest
  // this clears the bloom threshold on its own so the grid glows the way it
  // does in every reference frame, and stretch takes it well past that.
  float i = uBaseIntensity + s;

  gl_FragColor = vec4(c * i * cover, 1.0);
}
`;
